import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useMemo } from 'react';

import { BlogPost } from '#features/blog/components/BlogPost';
import type { BlogPostData } from '#features/blog/model/blog';
import { messages as enMessages } from '#features/i18n/locales/en/messages.po';
import { messages as ruMessages } from '#features/i18n/locales/ru/messages.po';

/** Actual reader UI with local article data. An empty id prevents view mutations. */
export const Route = createFileRoute('/{-$locale}/_layout/preview/blog-typography')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: (search: Record<string, unknown>) => ({
    lang: search.lang === 'en' ? ('en' as const) : ('ru' as const),
  }),
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
  component: TypographyPreview,
});

function TypographyPreview() {
  const { lang } = Route.useSearch();
  const i18n = useMemo(
    () => setupI18n({ locale: lang, messages: { en: enMessages, ru: ruMessages } }),
    [lang]
  );
  const ru = lang === 'ru';
  const headings = ru
    ? [
        'Как найти тиммейтов для совместной игры и договориться о времени сессии',
        'Выбор игры, языка общения и подходящего режима',
        'Что делать, если планы изменились',
      ]
    : [
        'How to find teammates and agree on a time for your next gaming session',
        'Choosing a game, communication language and the right mode',
        'What to do when your plans change',
      ];
  const paragraph = ru
    ? 'Выберите игру, расскажите о своих планах и найдите игроков, с которыми удобно общаться. Обсудите время сессии и предпочтения заранее, чтобы каждый участник понимал, когда и во что вы собираетесь играть.'
    : 'Choose a game, share your plans and find teammates you enjoy talking to. Discuss the session time and your preferences beforehand so every player knows when you plan to play and what you want to do together.';
  const post: BlogPostData = {
    id: '',
    readingTime: 3,
    title: ru
      ? 'Локальный пример статьи: поиск тиммейтов'
      : 'Local article example: finding teammates',
    content: JSON.stringify({
      blocks: headings.flatMap((text, index) => [
        { type: 'header', data: { text, level: index === 1 ? 3 : 2 } },
        ...Array.from({ length: 3 }, () => ({ type: 'paragraph', data: { text: paragraph } })),
      ]),
    }),
    date: '',
    updatedAt: '',
    keywords: '',
    ogDescription: '',
    ogTitle: ru ? 'Поиск тиммейтов' : 'Finding teammates',
    previewImage: '/game-bg/cs2.jpg',
    tags: '',
  };
  return (
    <I18nProvider i18n={i18n}>
      <BlogPost key={lang} post={post} slug="local-typography-example" />
    </I18nProvider>
  );
}
